import { DestroyRef, Injectable, afterNextRender, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivationEnd } from '@angular/router';

import { CDNCachingConfig, WidgetConfig } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { ofType } from '@frontend/sports/common/core/utils/extended-types';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { WidgetPage } from '@frontend/sports/types/components/widget';
import { EMPTY, Observable, interval, map, noop, switchMap, tap } from 'rxjs';

import { WidgetContextRefreshProcessor } from '../common/widget-context-refresh-processor';
import { WidgetComponent } from './widget-registry.service';

export abstract class WidgetRefreshTrigger {
    constructor(private config: WidgetConfig) {}

    asObservable(): Observable<void> {
        const time = this.config.pageRefreshInterval
            .filter(({ page }) => page === this.getPage())
            .map((page) => page.interval)
            .pop();

        if (time) {
            return interval(time * 1000).pipe(map(noop));
        }

        return EMPTY;
    }

    abstract getPage(): WidgetPage;
}

@Injectable({
    providedIn: 'root',
})
export class WidgetRefreshService {
    private processor = inject(WidgetContextRefreshProcessor);
    private cdnCacheConfig = inject(CDNCachingConfig);
    private destroyRef = inject(DestroyRef);

    private components = new Map<WidgetComponent<unknown>, undefined>();
    private triggers = new Map<RouteTag, WidgetRefreshTrigger>();

    constructor() {
        const router = inject(RouterEventsService);
        const productUtils = inject(SportsProductUtils);

        afterNextRender(() => {
            router.currentActivationEnd
                .pipe(
                    productUtils.filterSports(),
                    ofType(ActivationEnd),
                    switchMap((route) => this.triggers.get(route.snapshot.data.tag)?.asObservable() ?? EMPTY),
                    tap(() => this.refreshWidgets()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe();
        });
    }

    deregisterComponent(widgets: WidgetComponent<unknown>[]): void {
        for (const widget of widgets) {
            this.components.delete(widget);
        }
    }

    registerComponent(widgets: WidgetComponent<unknown>[]): void {
        for (const widget of widgets) {
            this.components.set(widget, undefined);
        }
    }

    registerTrigger(tag: RouteTag, trigger: WidgetRefreshTrigger): void {
        this.triggers.set(tag, trigger);
    }

    private refreshWidgets() {
        const components = [...this.components.keys()].filter((component) => component.config.refreshable);
        const contexts = components
            .filter((component) => !this.cdnCacheConfig.widgets.includes(component.config.type))
            .map((component) => component.getContext());

        if (contexts.length > 0) {
            this.processor.refresh(contexts).subscribe((widgets) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                for (const widget of widgets!) {
                    const component = components.find((current) => current.config.id === widget.id);

                    if (component) {
                        component.onResolve(widget, undefined, true, true);
                    }
                }
            });
        }
        components
            .filter((component) => this.cdnCacheConfig.widgets.includes(component.config.type) && component.config.refreshable)
            .forEach((component) => component.onResolve(component.config, undefined, true, true));
    }
}
